import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { Pencil } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import { ButtonIcon } from './ButtonIcon';
import type { EditButtonProps, IconButtonLinkProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Standardised edit button for table row actions
 *
 * Uses accent colour by default, customisable via color prop.
 *
 * @example
 * // Button mode - for opening edit drawers/modals
 * <Button.Edit onClick={() => handleEdit(record)} />
 *
 * @example
 * <Button.Edit
 *   onClick={handleEdit}
 *   tooltip="Edit settings"
 *   size="small"
 * />
 *
 * @example
 * // With custom colour
 * <Button.Edit onClick={handleEdit} color="orange" />
 */
export function ButtonEdit({
    onClick,
    disabled,
    tooltip = 'Edit',
    size = 'middle',
    color = 'accent',
}: EditButtonProps) {
    return (
        <ButtonIcon
            icon={<Pencil />}
            color={color}
            size={size}
            tooltip={tooltip}
            disabled={disabled}
            onClick={onClick}
        />
    );
}

/**
 * Base anchor component for edit links
 * Styled as an accent icon button using ButtonIcon CSS classes
 */
const ButtonEditLinkBase = forwardRef<HTMLAnchorElement, IconButtonLinkProps>(
    ({ color = 'accent', size = 'middle', tooltip = 'Edit', disabled, className, ...props }, ref) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [color, size, disabled, className],
        );

        const link = (
            <a ref={ref} aria-disabled={disabled} className={linkClasses} {...props}>
                <span className="btn-icon">
                    <Pencil size={iconSizes[size]} />
                </span>
            </a>
        );

        if (tooltip) {
            return <Tooltip title={tooltip}>{link}</Tooltip>;
        }

        return link;
    },
);

ButtonEditLinkBase.displayName = 'ButtonEditLinkBase';

/**
 * Button.Edit.Link - Type-safe router link styled as an edit icon button
 *
 * Uses TanStack Router for type-safe `to`, `params`, and `search` props.
 * Accent colour by default, customisable via color prop.
 *
 * @example
 * // Navigate to a claim edit page
 * <Button.Edit.Link
 *   to="/console/$orgId/claims/$claimId/edit"
 *   params={{ orgId: '1', claimId: '123' }}
 *   tooltip="Edit claim"
 * />
 */
export const ButtonEditLink: LinkComponent<typeof ButtonEditLinkBase> = createLink(ButtonEditLinkBase);
